import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import DefaultSEO from '@/components/DefaultSEO';
import { initMediaConfig } from "@/utils/mediaUrl";

// Hydrate CDN config once at module load (well before any image renders).
initMediaConfig();

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import ShopPage from "@/pages/ShopPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import NewCheckoutPage from "@/pages/NewCheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import DynamicPage from "@/pages/DynamicPage";
import AboutPage from "@/pages/AboutPage";
import ReBornPage from "@/pages/ReBornPage";
import SizeGuidePage from "@/pages/SizeGuidePage";
import AccountLayout from "@/pages/account/AccountLayout";
import AccountDashboard from "@/pages/account/AccountDashboard";
import OrdersPage from "@/pages/account/OrdersPage";
import OrderDetailPage from "@/pages/account/OrderDetailPage";
import WishlistPage from "@/pages/account/WishlistPage";
import AddressesPage from "@/pages/account/AddressesPage";
import ProfilePage from "@/pages/account/ProfilePage";
import MyReviewsPage from "@/pages/account/MyReviewsPage";
import MySupportTicketsPage from "@/pages/account/MySupportTicketsPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProductsList from "@/pages/admin/AdminProductsList";
import AdminCategoriesList from "@/pages/admin/AdminCategoriesList";
import AdminCategoryForm from "@/pages/admin/AdminCategoryForm";
import AdminBrandsList from "@/pages/admin/AdminBrandsList";
import AdminBrandForm from "@/pages/admin/AdminBrandForm";
import AdminOrdersList from "@/pages/admin/AdminOrdersList";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminCustomersList from "@/pages/admin/AdminCustomersList";
import AdminPageCategoriesList from "@/pages/admin/AdminPageCategoriesList";
import AdminPagesList from "@/pages/admin/AdminPagesList";
import AdminPageForm from "@/pages/admin/AdminPageForm";
import AdminSlidersList from "@/pages/admin/AdminSlidersList";
import AdminSliderForm from "@/pages/admin/AdminSliderForm";
import AdminMediaGallery from "@/pages/admin/AdminMediaGallery";
import AdminLocationsList from "@/pages/admin/AdminLocationsList";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminWarehouses from "@/pages/admin/AdminWarehouses";
import AdminAccessConfig from "@/pages/admin/AdminAccessConfig";
import AdminGeneral from "@/pages/admin/AdminGeneral";
import SearchResultsPage from "@/pages/SearchResultsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import EmailPreferencesPage from "@/pages/EmailPreferencesPage";
import PageView from "@/pages/PageView";
import ComingSoonPage from "@/pages/ComingSoonPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import CreateSupportTicket from "@/pages/CreateSupportTicket";
import SupportTicketChat from "@/pages/SupportTicketChat";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminSupportList from "@/components/admin/AdminSupportList";
import AdminSupportTicketDetail from "@/components/admin/AdminSupportTicketDetail";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminReviewModeration from "@/pages/admin/AdminReviewModeration";
import ProtectedRoute from "@/components/ProtectedRoute";
import StatusGuard from "@/components/StatusGuard";
import ScrollToTop from "@/components/ScrollToTop";
import ReBornFloatingBadge from "@/components/ReBornFloatingBadge";
import { Toaster } from "@/components/ui/toaster";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { LocalizationProvider } from "@/contexts/LocalizationContext";
import { GeneralSettingsProvider } from "@/contexts/GeneralSettingsContext";
import { CategoriesProvider } from "@/contexts/CategoriesContext";
import { SliderProvider } from "@/contexts/SliderContext";

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <DefaultSEO />
          <ReBornFloatingBadge />
          <LocalizationProvider>
            <GeneralSettingsProvider>
              <CategoriesProvider>
                <SliderProvider>
                  <CartProvider>
                  <WishlistProvider>
            <Routes>
              {/* Coming Soon Page - No Guard */}
              <Route path="/coming-soon" element={<ComingSoonPage />} />
              
              {/* All other routes with Status Guard */}
              <Route path="/*" element={
                <StatusGuard>
                  <Routes>
              {/* Public Routes with Header/Footer */}
              <Route path="/" element={
                <div className="page-home">
                  <Header />
                  <main className="min-h-screen bg-white homepage-main">
                    <Home />
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/shop" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <ShopPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/shop/:categorySlug" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <ShopPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/shop/:category" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <ShopPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/product/:slug" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <ProductDetailPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/search" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <SearchResultsPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/checkout" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <NewCheckoutPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/restablecer" element={<ResetPasswordPage />} />
              <Route path="/verificar" element={<VerifyEmailPage />} />
              <Route path="/baja" element={<EmailPreferencesPage />} />
              <Route path="/suscripcion" element={<EmailPreferencesPage />} />
              <Route path="/preferencias" element={<EmailPreferencesPage />} />
              <Route path="/checkout/success" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <CheckoutSuccessPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/order-confirmation/:orderNumber" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <OrderConfirmationPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/about" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <AboutPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/quienes-somos" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <AboutPage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/reborn" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <ReBornPage />
                  </main>
                  <Footer />
                </>
              } />
              {/* /contact, /faq, /shipping: real 301s to /page/contact-us,
                  /page/faqs, /page/shipping-returns now live in nginx
                  (deploy/nginx/cimonamia.conf) — these used to route to
                  dead legacy components (ContactPage/FAQPage/ShippingPage)
                  with hardcoded English placeholder content, disconnected
                  from the real admin-managed pages. nginx intercepts the
                  exact paths before they ever reach the SPA, so keeping
                  routes for them here would just be unreachable dead code. */}

              {/* Order Tracking - Public Route */}
              <Route path="/track/:orderId" element={
                <>
                  <Header />
                  <OrderTrackingPage />
                  <Footer />
                </>
              } />

              {/* Support Routes - Protected */}
              <Route path="/support/create" element={
                <ProtectedRoute>
                  <Header />
                  <CreateSupportTicket />
                  <Footer />
                </ProtectedRoute>
              } />
              
              <Route path="/support/ticket/:ticketId" element={
                <ProtectedRoute>
                  <Header />
                  <SupportTicketChat />
                  <Footer />
                </ProtectedRoute>
              } />
              
              <Route path="/size-guide" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <SizeGuidePage />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/newsletter" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <DynamicPage />
                  </main>
                  <Footer />
                </>
              } />
              
              {/* Dynamic CMS Pages Route */}
              <Route path="/page/:slug" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <PageView />
                  </main>
                  <Footer />
                </>
              } />
              
              {/* Account Routes (Protected) */}
              <Route path="/account" element={
                <ProtectedRoute>
                  <>
                    <Header />
                    <AccountLayout />
                    <Footer />
                  </>
                </ProtectedRoute>
              }>
                <Route index element={<AccountDashboard />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/:orderNumber" element={<OrderDetailPage />} />
                <Route path="reviews" element={<MyReviewsPage />} />
                <Route path="support" element={<MySupportTicketsPage />} />
                <Route path="wishlist" element={<WishlistPage />} />
                <Route path="addresses" element={<AddressesPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>
              
              {/* Admin Routes (Protected - Admin Only) */}
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProductsList />} />
                <Route path="categories" element={<AdminCategoriesList />} />
                <Route path="categories/new" element={<AdminCategoryForm />} />
                <Route path="categories/edit/:id" element={<AdminCategoryForm />} />
                <Route path="brands" element={<AdminBrandsList />} />
                <Route path="brands/new" element={<AdminBrandForm />} />
                <Route path="brands/edit/:id" element={<AdminBrandForm />} />
                <Route path="page-categories" element={<AdminPageCategoriesList />} />
                <Route path="pages" element={<AdminPagesList />} />
                <Route path="pages/new" element={<AdminPageForm />} />
                <Route path="pages/edit/:id" element={<AdminPageForm />} />
                <Route path="sliders" element={<AdminSlidersList />} />
                <Route path="sliders/new" element={<AdminSliderForm />} />
                <Route path="sliders/edit/:id" element={<AdminSliderForm />} />
                <Route path="media" element={<AdminMediaGallery />} />
                <Route path="orders" element={<AdminOrdersList />} />
                <Route path="orders/:orderNumber" element={<AdminOrderDetail />} />
                <Route path="customers" element={<AdminCustomersList />} />
                <Route path="settings/locations" element={<AdminLocationsList />} />
                <Route path="settings/shipping" element={<AdminShipping />} />
                <Route path="settings/warehouses" element={<AdminWarehouses />} />
                <Route path="settings/access" element={<AdminAccessConfig />} />
                <Route path="settings/general" element={<AdminGeneral />} />
                <Route path="support" element={<AdminSupportList />} />
                <Route path="support/:ticketId" element={<AdminSupportTicketDetail />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="reviews" element={<AdminReviewModeration />} />
              </Route>
              
              {/* 404 Page - Direct access */}
              <Route path="/404" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <NotFoundPage />
                  </main>
                  <Footer />
                </>
              } />

              {/* Dynamic Pages Catch-all - Must be last */}
              <Route path="/:slug" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <PageView />
                  </main>
                  <Footer />
                </>
              } />

              {/* 404 Not Found - Final catch-all for invalid routes */}
              <Route path="*" element={
                <>
                  <Header />
                  <main className="min-h-screen bg-white">
                    <NotFoundPage />
                  </main>
                  <Footer />
                </>
              } />
                  </Routes>
                </StatusGuard>
              } />
            </Routes>
            <Toaster />
          </WishlistProvider>
        </CartProvider>
      </SliderProvider>
      </CategoriesProvider>
      </GeneralSettingsProvider>
    </LocalizationProvider>
  </BrowserRouter>
  </div>
  </HelmetProvider>
);
}

export default App;
